(() => {
  "use strict";

  const CLIENT_PATH = "/portal/customer-auth/client.js";
  const modules = new WeakSet();
  let authLoad = null;

  function rootBase(root) {
    const raw = String(root?.dataset?.coreBase || "").trim();
    if (!raw) throw new Error("SH_CORE_BASE_UNCONFIGURED");

    const url = new URL(raw);

    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      (url.pathname !== "/" && url.pathname !== "")
    ) {
      throw new Error("SH_CORE_BASE_INVALID");
    }

    return url.origin;
  }

  function endpoint(root, path) {
    return rootBase(root) + path;
  }

  function status(root, message, state = "") {
    const node = root.querySelector("[data-sh-status]");
    if (!node) return;

    node.textContent = String(message || "").slice(0, 400);
    node.dataset.state = state;
  }

  function friendly(error) {
    const code = String(error?.message || error || "");

    if (code === "SH_CORE_BASE_UNCONFIGURED") {
      return "Palvelu ei ole juuri nyt käytettävissä. Yritä hetken kuluttua uudelleen.";
    }

    if (code === "SH_CORE_BASE_INVALID") {
      return "Palvelu ei ole juuri nyt käytettävissä. Yritä hetken kuluttua uudelleen.";
    }

    if (code.includes("CUSTOMER_ACCOUNT_LOGIN_REDIRECT")) {
      return "Siirrytään kirjautumaan…";
    }

    return "Toiminto epäonnistui. Tarkista tiedot ja yritä uudelleen.";
  }

  function loadAuth(root) {
    if (window.SHCustomerAccountAuth) {
      return Promise.resolve(window.SHCustomerAccountAuth);
    }

    if (authLoad) return authLoad;

    authLoad = new Promise((resolve, reject) => {
      const script = document.createElement("script");

      script.type = "module";
      script.src = endpoint(root, CLIENT_PATH);
      script.async = true;
      script.crossOrigin = "anonymous";

      script.onload = () =>
        window.SHCustomerAccountAuth
          ? resolve(window.SHCustomerAccountAuth)
          : reject(
              new Error("CUSTOMER_ACCOUNT_CLIENT_UNAVAILABLE")
            );

      script.onerror = () =>
        reject(
          new Error("CUSTOMER_ACCOUNT_CLIENT_UNAVAILABLE")
        );

      document.head.appendChild(script);
    }).catch((error) => {
      authLoad = null;
      throw error;
    });

    return authLoad;
  }

  async function publicJson(root, path, body) {
    const response = await fetch(endpoint(root, path), {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body),
      cache: "no-store",
      credentials: "omit"
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        String(data?.code || `HTTP_${response.status}`)
      );
    }

    return data;
  }

  async function authorized(root, path, init = {}) {
    const auth = await loadAuth(root);

    return auth.authorizedFetch(
      endpoint(root, path),
      {
        cache: "no-store",
        ...init
      }
    );
  }

  async function authorizedJson(root, path, body) {
    const response = await authorized(root, path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        String(data?.code || `HTTP_${response.status}`)
      );
    }

    return data;
  }

  async function authorizedEmptyPost(root, path) {
    const response = await authorized(root, path, {
      method: "POST"
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        String(data?.code || `HTTP_${response.status}`)
      );
    }

    return data;
  }

  function fields(form, allowed, booleans = []) {
    const set = new Set(allowed);
    const boolSet = new Set(booleans);
    const fd = new FormData(form);
    const out = {};

    for (const key of allowed) {
      const control = form.elements.namedItem(key);

      if (boolSet.has(key)) {
        out[key] = !!(control && control.checked);
        continue;
      }

      if (!fd.has(key)) continue;

      const value = String(
        fd.get(key) ?? ""
      ).trim();

      if (value !== "") {
        out[key] = value;
      }
    }

    for (const [key] of fd.entries()) {
      if (
        !set.has(key) &&
        !String(key).startsWith("_")
      ) {
        throw new Error("FORM_FIELD_NOT_ALLOWED");
      }
    }

    return out;
  }

  function bindConditionalOtherField(
    root,
    {
      selectName,
      otherName,
      otherValue
    }
  ) {
    const select =
      root.querySelector(
        `[name="${selectName}"]`
      );

    const wrapper =
      root.querySelector(
        `[data-sh-other-field="${otherName}"]`
      );

    const input =
      wrapper?.querySelector(
        `[name="${otherName}"]`
      );

    if (!select || !wrapper || !input) {
      throw new Error(
        "FORM_CLASSIFICATION_CONTROL_MISSING"
      );
    }

    const sync = () => {
      const active =
        select.value === otherValue;

      wrapper.hidden = !active;
      input.disabled = !active;
      input.required = active;

      if (!active) {
        input.value = "";
      }
    };

    select.addEventListener(
      "change",
      sync
    );

    sync();
  }

  function bindProviderClassificationFields(
    root
  ) {
    bindConditionalOtherField(
      root,
      {
        selectName: "city",
        otherName: "cityOther",
        otherValue: "Muu kaupunki"
      }
    );

    bindConditionalOtherField(
      root,
      {
        selectName: "industry",
        otherName: "industryOther",
        otherValue: "Muu toimiala"
      }
    );
  }

  function bindForm(
    root,
    {
      path,
      allowed,
      booleans = [],
      auth = false,
      transform = null
    }
  ) {
    const form =
      root.querySelector("form[data-sh-form]");

    if (!form) return;

    form.addEventListener(
      "submit",
      async (event) => {
        event.preventDefault();

        if (!form.reportValidity()) return;

        const submit =
          form.querySelector('[type="submit"]');

        if (submit) {
          submit.disabled = true;
        }

        status(
          root,
          "Lähetetään…",
          "busy"
        );

        try {
          let body = fields(
            form,
            allowed,
            booleans
          );

          if (transform) {
            body = transform(body, form);
          }

          const data = auth
            ? await authorizedJson(
                root,
                path,
                body
              )
            : await publicJson(
                root,
                path,
                body
              );

          status(
            root,
            data?.state === "UPDATED"
              ? "Tiedot päivitettiin."
              : "Tiedot vastaanotettiin onnistuneesti.",
            "success"
          );

          form.dispatchEvent(
            new CustomEvent(
              "sh:success",
              {
                detail: data
              }
            )
          );
        } catch (error) {
          status(
            root,
            friendly(error),
            "error"
          );
        } finally {
          if (submit) {
            submit.disabled = false;
          }
        }
      }
    );
  }

  function bindEmailSync(root) {
    for (
      const button of root.querySelectorAll(
        "[data-sh-verified-email-sync]"
      )
    ) {
      button.addEventListener(
        "click",
        async () => {
          button.disabled = true;

          status(
            root,
            "Varmistetaan sähköposti…",
            "busy"
          );

          try {
            await authorizedEmptyPost(
              root,
              "/api/native-forms/verified-email-change"
            );

            status(
              root,
              "Vahvistettu sähköposti synkronoitiin.",
              "success"
            );
          } catch (error) {
            status(
              root,
              friendly(error),
              "error"
            );
          } finally {
            button.disabled = false;
          }
        }
      );
    }
  }

  async function mountCallback(root) {
    status(
      root,
      "Viimeistellään kirjautumista…",
      "busy"
    );

    try {
      const auth = await loadAuth(root);

      const token =
        await auth.getAccessToken({
          interactive: false,
          returnTo: window.location.href
        });

      if (token) {
        status(
          root,
          "Kirjautuminen valmis.",
          "success"
        );

        const target =
          root.dataset.successPath || "/";

        window.setTimeout(
          () =>
            window.location.assign(target),
          250
        );
      } else {
        status(
          root,
          "Kirjautuminen ei ole vielä valmis.",
          "idle"
        );
      }
    } catch (error) {
      status(
        root,
        friendly(error),
        "error"
      );
    }

    const button =
      root.querySelector("[data-sh-login]");

    if (button) {
      button.addEventListener(
        "click",
        async () => {
          try {
            const auth =
              await loadAuth(root);

            await auth.beginLogin({
              returnTo:
                root.dataset.successPath ||
                "/"
            });
          } catch (error) {
            status(
              root,
              friendly(error),
              "error"
            );
          }
        }
      );
    }
  }

  function mountActivation(root) {
    for (
      const button of root.querySelectorAll(
        "[data-membership-kind]"
      )
    ) {
      button.addEventListener(
        "click",
        async () => {
          const kind =
            button.dataset.membershipKind;

          if (
            !new Set([
              "monthly",
              "yearly"
            ]).has(kind)
          ) {
            return;
          }

          for (
            const b of root.querySelectorAll(
              "[data-membership-kind]"
            )
          ) {
            b.disabled = true;
          }

          status(
            root,
            "Tarkistetaan jäsenyyden aktivointioikeus…",
            "busy"
          );

          try {
            const data =
              await authorizedJson(
                root,
                "/portal/api/membership/activation",
                {
                  membershipKind: kind
                }
              );

            const cart = data?.cart;

            if (
              !data?.ok ||
              !cart ||
              cart.action !==
                "/cart/add.js" ||
              cart.method !== "POST" ||
              !cart.body ||
              typeof data.checkoutPath !==
                "string" ||
              !data.checkoutPath.startsWith(
                "/"
              )
            ) {
              throw new Error(
                "ACTIVATION_RESPONSE_INVALID"
              );
            }

            const cartResponse =
              await fetch(
                cart.action,
                {
                  method: cart.method,
                  headers: {
                    "Content-Type":
                      "application/json",
                    Accept:
                      "application/json"
                  },
                  body: JSON.stringify(
                    cart.body
                  ),
                  credentials:
                    "same-origin",
                  cache: "no-store"
                }
              );

            if (!cartResponse.ok) {
              throw new Error(
                "CART_ADD_FAILED"
              );
            }

            window.location.assign(
              data.checkoutPath
            );
          } catch (error) {
            status(
              root,
              friendly(error),
              "error"
            );

            for (
              const b of root.querySelectorAll(
                "[data-membership-kind]"
              )
            ) {
              b.disabled = false;
            }
          }
        }
      );
    }
  }

  async function mountPortalGateway(root) {
    const dashboards = Array.from(
      document.querySelectorAll(
        "[data-sh-dashboard-gated][data-sh-portal-dashboard]"
      )
    );

    const choices =
      root.querySelector(
        "[data-sh-portal-choices]"
      );

    const activate = (type) => {
      let matched = false;

      for (const dashboard of dashboards) {
        const show =
          dashboard.dataset
            .shPortalDashboard === type;

        dashboard.hidden = !show;

        if (show) {
          matched = true;
        }
      }

      if (!matched) {
        throw new Error(
          "PORTAL_DASHBOARD_UNAVAILABLE"
        );
      }

      root.hidden = true;
    };

    status(
      root,
      "Avataan portaalia…",
      "busy"
    );

    try {
      const response = await authorized(
        root,
        "/portal/api/context",
        {
          method: "GET"
        }
      );

      const data =
        await response
          .json()
          .catch(() => null);

      if (!response.ok) {
        throw new Error(
          String(
            data?.code ||
              `HTTP_${response.status}`
          )
        );
      }

      if (
        !data ||
        !Array.isArray(data.scopes)
      ) {
        throw new Error(
          "PORTAL_CONTEXT_INVALID"
        );
      }

      const types = Array.from(
        new Set(
          data.scopes
            .map((scope) =>
              String(
                scope?.resourceType || ""
              )
            )
            .filter(
              (type) =>
                type === "provider" ||
                type === "organization"
            )
        )
      );

      if (types.length === 1) {
        activate(types[0]);
        return;
      }

      if (
        types.length > 1 &&
        choices
      ) {
        choices.hidden = false;

        for (
          const button of choices.querySelectorAll(
            "[data-sh-portal-choice]"
          )
        ) {
          const type = String(
            button.dataset
              .shPortalChoice || ""
          );

          button.hidden =
            !types.includes(type);

          button.addEventListener(
            "click",
            () => activate(type),
            {
              once: true
            }
          );
        }

        status(
          root,
          "Valitse portaali.",
          "idle"
        );

        return;
      }

      status(
        root,
        "Portaalia ei löytynyt tälle käyttäjätilille.",
        "error"
      );
    } catch (error) {
      status(
        root,
        friendly(error),
        "error"
      );
    }
  }

  function mountOrganizationSearch(root) {
    const form =
      root.querySelector(
        "form[data-sh-org-search]"
      );

    const results =
      root.querySelector(
        "[data-sh-results]"
      );

    if (!form || !results) return;

    form.addEventListener(
      "submit",
      async (event) => {
        event.preventDefault();

        const q = String(
          new FormData(form).get("q") ||
            ""
        ).trim();

        if (
          q.length < 2 ||
          q.length > 120
        ) {
          status(
            root,
            "Kirjoita vähintään kaksi merkkiä.",
            "error"
          );

          return;
        }

        status(
          root,
          "Haetaan…",
          "busy"
        );

        results.replaceChildren();

        try {
          const response =
            await authorized(
              root,
              `/portal/api/benefit/organizations?q=${encodeURIComponent(
                q
              )}`,
              {
                method: "GET"
              }
            );

          const data =
            await response
              .json()
              .catch(() => null);

          if (!response.ok) {
            throw new Error(
              String(
                data?.code ||
                  `HTTP_${response.status}`
              )
            );
          }

          const list =
            Array.isArray(
              data?.results
            )
              ? data.results.slice(
                  0,
                  100
                )
              : [];

          if (!list.length) {
            results.textContent =
              "Ei osumia.";
          } else {
            const ul =
              document.createElement(
                "ul"
              );

            for (
              const item of list
            ) {
              const li =
                document.createElement(
                  "li"
                );

              li.textContent =
                String(
                  item?.organizationName ||
                    "Organisaatio"
                ).slice(
                  0,
                  240
                );

              ul.appendChild(li);
            }

            results.appendChild(ul);
          }

          status(
            root,
            `Osumia: ${list.length}`,
            "success"
          );
        } catch (error) {
          status(
            root,
            friendly(error),
            "error"
          );
        }
      }
    );
  }

  function bindOrganizationIdentityLookup(root) {
    const form =
      root.querySelector("form[data-sh-form]");

    const businessInput =
      form?.elements.namedItem("businessId");

    const officialNameInput =
      form?.elements.namedItem("officialName");

    const lookupStatus =
      root.querySelector(
        "[data-sh-organization-identity-status]"
      );

    if (
      !form ||
      !businessInput ||
      !officialNameInput ||
      !lookupStatus
    ) {
      throw new Error(
        "ORGANIZATION_IDENTITY_CONTROLS_MISSING"
      );
    }

    const businessIdPattern =
      /^[0-9]{7}-[0-9]$/u;

    let timer = null;
    let requestVersion = 0;
    let autoFilledName = "";

    const setLookupStatus = (
      message,
      state = ""
    ) => {
      const text =
        String(message || "").slice(0, 300);

      lookupStatus.textContent = text;
      lookupStatus.dataset.state = state;
      lookupStatus.hidden = text === "";
    };

    const clearTimer = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const clearAutoFilledName = () => {
      if (
        autoFilledName &&
        officialNameInput.value ===
          autoFilledName
      ) {
        officialNameInput.value = "";
      }

      autoFilledName = "";
      officialNameInput.readOnly = false;
    };

    const currentBusinessId = () =>
      String(businessInput.value || "").trim();

    const lookup = async () => {
      clearTimer();

      const businessId =
        currentBusinessId();

      if (!businessIdPattern.test(businessId)) {
        return;
      }

      const version =
        ++requestVersion;

      businessInput.setCustomValidity("");
      clearAutoFilledName();

      setLookupStatus(
        "Tarkistetaan YTJ/PRH-tiedoista…",
        "busy"
      );

      try {
        const url =
          new URL(
            endpoint(
              root,
              "/api/native-forms/organization-join"
            )
          );

        url.searchParams.set(
          "businessId",
          businessId
        );

        const response =
          await fetch(url.toString(), {
            method: "GET",
            headers: {
              Accept: "application/json"
            },
            cache: "no-store",
            credentials: "omit"
          });

        const data =
          await response
            .json()
            .catch(() => null);

        if (
          version !== requestVersion ||
          businessId !== currentBusinessId()
        ) {
          return;
        }

        if (!response.ok) {
          if (
            response.status === 400 ||
            data?.code === "INVALID_REQUEST"
          ) {
            businessInput.setCustomValidity(
              "Tarkista Y-tunnus."
            );

            setLookupStatus(
              "Tarkista Y-tunnus.",
              "error"
            );

            return;
          }

          if (
            data?.code === "PRH_UNAVAILABLE"
          ) {
            setLookupStatus(
              "YTJ/PRH-tarkistus ei ole juuri nyt käytettävissä. Yritä hetken kuluttua uudelleen.",
              "error"
            );

            return;
          }

          throw new Error(
            String(
              data?.code ||
                "HTTP_" + response.status
            )
          );
        }

        if (
          data?.state === "FOUND" &&
          typeof data.officialName ===
            "string" &&
          data.officialName.trim()
        ) {
          officialNameInput.value =
            data.officialName.trim();

          autoFilledName =
            officialNameInput.value;

          officialNameInput.readOnly = true;

          setLookupStatus(
            "Virallinen nimi haettu YTJ/PRH-tiedoista.",
            "success"
          );

          return;
        }

        if (
          data?.state === "NOT_FOUND" &&
          data.manualFallback === true
        ) {
          officialNameInput.readOnly = false;

          setLookupStatus(
            "Y-tunnusta ei löytynyt YTJ/PRH-tiedoista. Syötä organisaation virallinen nimi.",
            "manual"
          );

          return;
        }

        throw new Error(
          "ORGANIZATION_IDENTITY_LOOKUP_INVALID"
        );
      }
      catch {
        if (version !== requestVersion) {
          return;
        }

        clearAutoFilledName();

        setLookupStatus(
          "YTJ/PRH-tarkistus ei ole juuri nyt käytettävissä. Yritä hetken kuluttua uudelleen.",
          "error"
        );
      }
    };

    businessInput.addEventListener(
      "input",
      () => {
        clearTimer();
        requestVersion += 1;
        businessInput.setCustomValidity("");
        clearAutoFilledName();
        setLookupStatus("");

        if (
          businessIdPattern.test(
            currentBusinessId()
          )
        ) {
          timer =
            window.setTimeout(
              lookup,
              450
            );
        }
      }
    );

    businessInput.addEventListener(
      "blur",
      () => {
        const businessId =
          currentBusinessId();

        if (!businessId) return;

        if (
          !businessIdPattern.test(
            businessId
          )
        ) {
          clearTimer();

          businessInput.setCustomValidity(
            "Tarkista Y-tunnus."
          );

          setLookupStatus(
            "Tarkista Y-tunnus muodossa 1234567-8.",
            "error"
          );

          return;
        }

        lookup();
      }
    );
  }

  function bindOrganizationVerificationFields(root) {
    const form =
      root.querySelector("form[data-sh-form]");

    const radios =
      Array.from(
        root.querySelectorAll(
          '[name="_verificationMethod"]'
        )
      );

    const domainWrapper =
      root.querySelector(
        "[data-sh-work-email-domain-field]"
      );

    const domainInput =
      form?.elements.namedItem(
        "workEmailDomain"
      );

    if (
      !form ||
      radios.length !== 2 ||
      !domainWrapper ||
      !domainInput
    ) {
      throw new Error(
        "ORGANIZATION_VERIFICATION_CONTROLS_MISSING"
      );
    }

    const sync = () => {
      const selected =
        radios.find(
          (radio) => radio.checked
        )?.value || "";

      const useDomain =
        selected === "workEmailDomain";

      domainWrapper.hidden =
        !useDomain;

      domainInput.disabled =
        !useDomain;

      domainInput.required =
        useDomain;

      if (!useDomain) {
        domainInput.value = "";
      }

      for (const radio of radios) {
        radio
          .closest(
            "[data-sh-verification-card]"
          )
          ?.classList.toggle(
            "is-selected",
            radio.checked
          );
      }
    };

    for (const radio of radios) {
      radio.addEventListener(
        "change",
        sync
      );
    }

    sync();
  }

  function mount(root) {
    if (modules.has(root)) return;

    modules.add(root);

    const mod =
      root.dataset.shCoreModule;

    if (mod === "callback") {
      return mountCallback(root);
    }

    if (mod === "portal-gateway") {
      return mountPortalGateway(root);
    }

    if (
      mod ===
      "membership-activation"
    ) {
      return mountActivation(root);
    }

    if (mod === "provider-join") {
      bindProviderClassificationFields(root);

      bindForm(root, {
        path:
          "/api/native-forms/provider-join",
        allowed: [
          "email",
          "companyName",
          "businessId",
          "billingAddress",
          "addressExtra",
          "billingPostcode",
          "city",
          "cityOther",
          "industry",
          "industryOther",
          "companyPhone",
          "firstName",
          "lastName",
          "contactPhone",
          "marketingConsent",
          "termsAccepted"
        ],
        booleans: [
          "marketingConsent",
          "termsAccepted"
        ]
      });
    }

    if (
      mod ===
      "organization-join"
    ) {
      bindOrganizationIdentityLookup(root);
      bindOrganizationVerificationFields(root);

      bindForm(root, {
        path:
          "/api/native-forms/organization-join",
        allowed: [
          "addressLine",
          "businessId",
          "city",
          "contactEmail",
          "firstName",
          "lastName",
          "eligiblePopulationBand",
          "marketingConsent",
          "memberCard",
          "officialName",
          "postalCode",
          "termsAccepted",
          "workEmailDomain",
          "addressExtra",
          "contactPhone",
          "organizationPhone"
        ],
        booleans: [
          "marketingConsent",
          "memberCard",
          "termsAccepted"
        ],
        transform(body, form) {
          const method =
            String(
              new FormData(form).get(
                "_verificationMethod"
              ) || ""
            );

          if (
            method === "workEmailDomain"
          ) {
            body.memberCard = false;

            if (!body.workEmailDomain) {
              throw new Error(
                "ORGANIZATION_WORK_EMAIL_DOMAIN_REQUIRED"
              );
            }

            return body;
          }

          if (
            method === "memberCard"
          ) {
            body.memberCard = true;
            delete body.workEmailDomain;
            return body;
          }

          throw new Error(
            "ORGANIZATION_VERIFICATION_METHOD_REQUIRED"
          );
        }
      });
    }

    if (
      mod ===
      "provider-update"
    ) {
      bindForm(root, {
        path:
          "/api/native-forms/provider-update",
        auth: true,
        allowed: [
          "addressLine",
          "companyName",
          "postalCode",
          "bookingUrl",
          "companyPhone",
          "contactPhone",
          "description",
          "logoUrl"
        ]
      });
    }

    if (
      mod ===
      "organization-update"
    ) {
      bindForm(root, {
        path:
          "/api/native-forms/organization-update",
        auth: true,
        allowed: [
          "addressLine",
          "city",
          "firstName",
          "lastName",
          "contactPhone",
          "eligiblePopulationBand",
          "organizationPhone",
          "postalCode"
        ],
        transform(body, form) {
          const hasFirstName =
            Object.prototype.hasOwnProperty.call(
              body,
              "firstName"
            );
          const hasLastName =
            Object.prototype.hasOwnProperty.call(
              body,
              "lastName"
            );

          if (hasFirstName !== hasLastName) {
            throw new Error(
              "ORGANIZATION_UPDATE_CONTACT_NAME_PAIR_REQUIRED"
            );
          }

          for (
            const box of form.querySelectorAll(
              "[data-clear-field]"
            )
          ) {
            if (box.checked) {
              body[
                box.dataset.clearField
              ] = "";
            }
          }

          if (
            !Object.keys(body).length
          ) {
            throw new Error(
              "ORGANIZATION_UPDATE_BODY_EMPTY"
            );
          }

          return body;
        }
      });
    }

    if (
      mod === "provider-move"
    ) {
      bindForm(root, {
        path:
          "/api/native-forms/provider-move-request",
        auth: true,
        allowed: [
          "targetCitySlug",
          "targetIndustrySlug",
          "addressLine",
          "postalCode",
          "city",
          "industry"
        ],
        transform(body) {
          if (
            !body.targetCitySlug ||
            !body.targetIndustrySlug
          ) {
            throw new Error(
              "MOVE_TARGET_INVALID"
            );
          }

          return {
            ...body,
            targetSlotKey:
              `${body.targetCitySlug}--${body.targetIndustrySlug}`
          };
        }
      });
    }

    if (
      mod ===
      "organization-search"
    ) {
      mountOrganizationSearch(root);
    }

    bindEmailSync(root);
  }

  function boot() {
    document
      .querySelectorAll(
        "[data-sh-core-module]"
      )
      .forEach(mount);
  }

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  window.SHCoreStorefront =
    Object.freeze({
      boot
    });
})();